import { useRouter } from 'next/router'
import { useState, useEffect } from 'react'
import Link from 'next/link'
import { CheckoutContextProvider } from 'utils/checkout'
import Checkout from 'components/checkout/checkout'
import Panel from 'components/panel/panel'
import Meta from 'components/meta/meta'
import Heading from 'components/heading/heading'
import Btn from 'components/btn/btn'
import Input from 'components/input/input/input'
import Checkbox from 'components/input/checkbox/checkbox'
import InputGroup from 'components/input-group/input-group'
import banks from 'utils/data/banks'
import { cn, getRoute, parseFormEvent } from 'utils/helpers'
import styles from 'styles/page/pirkimas-apmokejimas.module.scss'
import useAppState from 'utils/state'
import useCheckout from 'utils/checkout'
import { trackSelectPayment } from 'utils/analytics/events'
import RevolutCheckout from '@revolut/checkout'
import { useModal } from 'components/modals/provider'
import Search from 'components/input/search/search'
import countries from 'utils/data/countries'
import { get } from 'utils/api'

const env = process.env.NEXT_PUBLIC_APP_ENV

const hasLogo = [
    'swedbank',
    'luminor',
    'medicinosbankas',
    'neofinance',
    'paysera',
    'ratounija',
    'revolut',
    'seb',
    'siauliubankas',
    'kreda',
    'lcku',
    'citadele',
    'card',
    'paystrax'
]

const getLogo = bank => {
    if (hasLogo.indexOf(bank.code) > -1) {
        return `/banks/${bank.code}.${bank.code == 'card' || bank.code == 'paystrax' ? 'svg' : 'png'}`
    }
    return bank.logo
}

function Payment() {
    const router = useRouter()
    const { cart, updateCartDetails, showToast, clearCurrentCartAccount, setRedirecting, showLoader } = useAppState()
    const { showMessage } = useModal()
    const { startPayment, setupLoading } = useCheckout()
    const [info, setInfo] = useState(cart.details.payment)

    useEffect(() => {
        if (!setupLoading) {
            const data = { ...cart.details.payment }
            if (!cart.details.payment.country_code) {
                let country_code, postal_code
                if (cart && cart.details && cart.details.delivery && cart.details.delivery.delivery_type == 'outside') {
                    country_code = cart.details.delivery.outside_country
                    postal_code = cart.details.delivery.outside_postal_code
                } else {
                    country_code = 'LT'
                }
                if (
                    cart &&
                    cart.details &&
                    cart.details.delivery &&
                    cart.details.delivery.delivery_type == 'lt' &&
                    cart.details.delivery.courier_zip
                ) {
                    postal_code = cart.details.delivery.courier_zip
                }
                if (postal_code || country_code) {
                    data.country_code = country_code
                    data.postal_code = postal_code
                    if (!data.company_country_code) {
                        data.company_country_code = country_code
                    }
                }
            }
            setInfo(data)
        }
    }, [setupLoading])

    const finishOrder = async code => {
        const response = await get('revolut-accept', { code })
        if (response.status == 200) {
            // clearCurrentCartAccount()
            window.location.href = `${getRoute('checkout-success')}?code=${code}`
        } else if (response.data && response.data.message) {
            showMessage(response.data.message)
        }
    }

    const handleSubmit = async e => {
        e.preventDefault()
        const data = { ...cart.details, payment: info }
        updateCartDetails(data)
        showLoader(true)
        trackSelectPayment(cart.items)
        const response = await startPayment(data)
        showLoader(false)
        if (response.error) {
            showToast(response.error, 'warning')
        } else if (response.data.message) {
            router.push(getRoute('checkout-success'))
            // clearCurrentCartAccount()
        } else {
            if (response.data.revolut) {
                const { revolut: dataToken, item } = response.data
                const { code } = item
                const revolutEnv = env === 'prod' ? 'prod' : 'sandbox'
                const RC = await RevolutCheckout(dataToken, revolutEnv)
                const { email } = cart.details.information

                // https://developer.revolut.com/docs/accept-payments/tutorials/test-in-the-sandbox-environment/test-cards/
                // 4929420573595709
                // 5281438801804148
                // 12/22 123 01102
                // declined - 4242424242424242

                RC.payWithPopup({
                    email,
                    billingAddress: {
                        countryCode: info.country_code,
                        postcode: info.postal_code
                    },
                    locale: 'lt',
                    onSuccess() {
                        finishOrder(code)
                    },
                    onError() {
                        console.log(message)
                        showMessage('Įvyko nenumatyta klaida :/')
                    },
                    onCancel() {
                        showMessage('Jūs atšaukėte užsakymą :/')
                    }
                })
            } else {
                if (env === 'local') {
                    router.push('checkout-success')
                } else {
                    const dataToken = response.data.neopay
                    const userRedirectURL = `https://psd2.neopay.lt/widget.html?${dataToken}`
                    window.location.href = userRedirectURL
                }
                const time = new Date().getTime()
                setRedirecting(time)
                clearCurrentCartAccount()
            }
        }
    }

    const handleChange = e => {
        const { name, value } = parseFormEvent(e)
        setInfo({ ...info, [name]: value })
        // new
        const data = { ...cart.details, payment: { ...info, [name]: value } }
        updateCartDetails(data)
    }

    return (
        <Checkout summary>
            <Meta
                title="Apmokėjimas"
                description="Užsakymo apmokėjimo informacijos pateikimas ir mokėjimo būdo pasirinkimas."
            />
            <Panel className="mb48" desktop>
                <form onSubmit={handleSubmit}>
                    <Heading tag="h2" size="24" className="mb36">
                        Apmokėjimas
                    </Heading>
                    <div className="mb48">
                        <div className={styles.banks}>
                            <input
                                className={styles.banksHiddenRequired}
                                type="text"
                                value={info.bank}
                                tabIndex={-1}
                                inputMode="none"
                                autoComplete="off"
                                required
                                onChange={() => {}}
                            />
                            {banks.aspsps.map(bank => (
                                <div key={bank.code}>
                                    <div
                                        onClick={() => setInfo({ ...info, bank: bank.bic })}
                                        className={cn(
                                            info.bank == bank.bic && styles.active,
                                            styles[`bank${bank.code}`]
                                        )}
                                        style={{ backgroundImage: `url(${getLogo(bank)})` }}
                                    >
                                        <span>
                                            {bank.code == 'card' || bank.code == 'paystrax'
                                                ? 'Mokėjimo kortelė'
                                                : bank.code == 'other'
                                                ? 'Kitas bankas'
                                                : ''}
                                        </span>
                                    </div>
                                </div>
                            ))}
                            {env !== 'prod' && (
                                <div>
                                    <div
                                        onClick={() => setInfo({ ...info, bank: 'test' })}
                                        className={cn(info.bank == 'test' && styles.active)}
                                    >
                                        <span>Test</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    {info.bank === 'revolut' && (
                        <InputGroup>
                            <Search
                                label="Mokėtojo šalis"
                                options={countries}
                                value={info.country_code}
                                name="country_code"
                                onChange={handleChange}
                                required
                            />
                            <Input
                                label="Mokėtojo pašto kodas"
                                name="postal_code"
                                type="text"
                                value={info.postal_code}
                                onChange={handleChange}
                                required
                            />
                        </InputGroup>
                    )}
                    <Checkbox
                        type="checkbox"
                        name="is_company"
                        label="Perka įmonė (reikalinga sąskaita-faktūra)"
                        value={info.is_company}
                        onChange={handleChange}
                    />
                    {!!info.is_company && (
                        <>
                            <InputGroup>
                                <Input
                                    type="text"
                                    name="company_name"
                                    label="Įmonės pavadinimas"
                                    value={info.company_name}
                                    onChange={handleChange}
                                    required
                                />
                                <Input
                                    type="text"
                                    name="company_address"
                                    label="Įmonės adresas"
                                    value={info.company_address}
                                    onChange={handleChange}
                                    required
                                />
                            </InputGroup>
                            <InputGroup>
                                <Search
                                    label="Šalis"
                                    options={countries}
                                    value={info.company_country_code}
                                    name="company_country_code"
                                    onChange={handleChange}
                                    required
                                />
                                <Input
                                    type="text"
                                    name="company_code"
                                    label="Įmonės kodas"
                                    value={info.company_code}
                                    onChange={handleChange}
                                    required
                                />
                                <Input
                                    type="text"
                                    name="company_vat"
                                    label="PVM kodas"
                                    value={info.company_vat}
                                    onChange={handleChange}
                                />
                            </InputGroup>
                        </>
                    )}
                    <Checkbox
                        name="subscribe"
                        label="Prenumeruoti naujienas"
                        value={info.subscribe}
                        onChange={handleChange}
                    />
                    <Checkbox
                        name="agree"
                        label={
                            <>
                                <span>Perskaičiau ir sutinku su </span>
                                <Link href={getRoute('purchase-sale-rules')}>
                                    <a target="_blank">taisyklėmis</a>
                                </Link>{' '}
                                ir{' '}
                                <Link href={getRoute('privacy-policy')}>
                                    <a target="_blank">privatumo politika</a>
                                </Link>
                                .
                            </>
                        }
                        value={info.agree}
                        onChange={handleChange}
                        required
                    />
                    <Btn style="yellow" block type="submit">
                        Apmokėti
                    </Btn>
                </form>
            </Panel>
        </Checkout>
    )
}

// {info.bank === 'CBVILT2X' && (
//     <Infoblock className="mb30">
//         DĖMESIO: dėl laikinų SEB sistemos sutrikimų prie elektroninės bankininkystės turėsite
//         prisijungti du kartus.
//     </Infoblock>
// )}

export default function Page() {
    return (
        <CheckoutContextProvider>
            <Payment />
        </CheckoutContextProvider>
    )
}
